import { useEffect, useMemo, useState } from 'react'
import {
  CircleCheck, CirclePlus, CircleX, Clock, Eye, Hourglass, List, LoaderCircle, OctagonPause, Trash2,
} from 'lucide-react'
import ConfirmDialog from '../../components/ConfirmDialog'
import Button from '../../components/Button'
import TextField from '../../components/TextField'
import DataTable from '../../components/DataTable'
import { useOnChange, useRemembered } from '../../components/listMemory'
import { usePageParam } from '../../components/useListParams'
import IconButton from '../../components/IconButton'
import Drawer from '../../components/Drawer'
import FilterBar from '../../components/FilterBar'
import Modal from '../../components/Modal'
import PageHeader from '../../components/PageHeader'
import StatStrip from '../../components/StatStrip'
import Select from '../../components/Select'
import { useToast } from '../../components/toastContext'
import StatusBadge from '../../components/StatusBadge'
import {
  MESSAGE_SORTABLE,
  BATCH_SORTABLE,
  deleteBatch,
  getNotificationsLogPage,
  listBatchMessages,
  listBatches,
  resolveUncertainDelivery,
} from './systemLogsService'
import { notificationsLogStrings as S } from './strings'
import './systemLogs.css'
import { serverDetail } from '../../lib/i18n'
import { displayDate } from '../../lib/dates'

/* Badge tones for the batch/message state chips. `New` and `Pending` are the two
   states the new-API handlers write before the job picks a batch up — the log
   shows them, so they get a chip like the rest. `New` wears `primary` because
   that is what a new incident wears everywhere else in the app. */
const STATE_TONE = {
  New: 'primary',
  Pending: 'warning',
  Waiting: 'warning',
  Running: 'primary',
  Stopped: 'dark',
  Finished: 'success',
  Failed: 'danger',
  Succeeded: 'success',
  Skipped: 'dark',
  Uncertain: 'warning',
  Dispatching: 'primary',
}

/* THE BAND READS AS THE LIFECYCLE, so the hue is the meaning-class and the glyph
   is the exact state. A batch is created (violet), sits in the queue (amber —
   `Pending` and `Waiting` are the same fact from two writers, so they share the
   hue and differ by glyph), goes out (blue), and ends with a verdict (green /
   red). `Stopped` names no tone on purpose: a halted batch is inert and the
   band's default grey ring says exactly that. The order here is the order the
   band shows them in, whatever order the API's GroupBy returns. */
const BAND_STATE = {
  New: { tone: 'violet', icon: CirclePlus },
  Pending: { tone: 'warning', icon: Hourglass },
  Waiting: { tone: 'warning', icon: Clock },
  Running: { tone: 'info', icon: LoaderCircle },
  Stopped: { icon: OctagonPause },
  Finished: { tone: 'success', icon: CircleCheck },
  Succeeded: { tone: 'success', icon: CircleCheck },
  Failed: { tone: 'danger', icon: CircleX },
  Uncertain: { tone: 'warning', icon: OctagonPause },
}
const BAND_ORDER = Object.keys(BAND_STATE)

/** The view builds this select from the NotificationEventStatus enum. */
const STATE_OPTIONS = Object.entries(S.states).map(([value, label]) => ({ value, label }))

const asDate = (iso) => (iso ? displayDate(iso.slice(0, 10)) : '')
const asDateTime = (iso) => displayDate(iso)

/** سجل التنبيهات — Views/NotificationsDetails/Index.cshtml. */
export default function NotificationsLogPage() {
  const toast = useToast()
  const [rows, setRows] = useState([])
  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(null)
  const [total, setTotal] = useState(0)
  const [stateCounts, setStateCounts] = useState([])
  const [page, setPage] = usePageParam()
  const [pageSize, setPageSize] = useRemembered('notifications-log:pageSize', 25)
  /* The order, owned here with the page; the default is the handler's own, latest scheduled first. */
  const [sort, setSort] = useRemembered('notifications-log:sort', { key: 'scheduledStartDate', dir: 'desc' })
  const [reloadKey, setReloadKey] = useState(0)
  /* THE TWO FILTERS DECIDE WHICH BATCHES THESE ARE, so they leave the screen with the page:
     the address came back saying `?page=4` and the type that produced it was gone, which is
     page 4 of a different log. `draft` is what the selects show — without it they read as
     empty over a list that is still filtered. */
  const [filters, setFilters] = useRemembered('notifications-log:filters', { type: '', status: '' })
  const [draft, setDraft] = useRemembered('notifications-log:draft', { type: '', status: '' })

  const [openBatch, setOpenBatch] = useState(null)
  const [messages, setMessages] = useState([])
  const [messagesLoading, setMessagesLoading] = useState(false)
  const [messagesError, setMessagesError] = useState(null)
  const [messagesPage, setMessagesPage] = useState(1)
  const [messagesPageSize, setMessagesPageSize] = useState(25)
  /* Null is the handler's own order — newest message first — which no column shows. */
  const [messagesSort, setMessagesSort] = useState(null)
  const [messagesTotal, setMessagesTotal] = useState(0)
  const [deliveryTrace, setDeliveryTrace] = useState('')
  const [reviewBusy, setReviewBusy] = useState(false)
  const [preview, setPreview] = useState(null)

  const [pendingDelete, setPendingDelete] = useState(null)

  useOnChange(() => setPage(1), [filters.type, filters.status])

  useEffect(() => {
    const controller = new AbortController()
    getNotificationsLogPage(controller.signal)
      .then((items) => !controller.signal.aborted && setTemplates(items))
      .catch((error) => !controller.signal.aborted && toast.error(serverDetail(error?.message) || S.loadFailed))
    return () => controller.abort()
  }, [toast])

  useEffect(() => {
    const controller = new AbortController()
    setLoading(true)
    listBatches({ ...filters, page, pageSize, sort }, controller.signal)
      .then((response) => {
        if (controller.signal.aborted) return
        setRows(response.rows)
        setTotal(response.total)
        setStateCounts(response.stateCounts)
        setLoadError(null)
        // Deleting the last batch on a page returns to the last remaining page.
        if (page > 1 && response.total <= (page - 1) * pageSize) {
          setPage(Math.max(1, Math.ceil(response.total / pageSize)))
        }
      })
      .catch((error) => {
        if (controller.signal.aborted) return
        setLoadError(error)
        setRows([])
        setTotal(0)
        setStateCounts([])
      })
      .finally(() => !controller.signal.aborted && setLoading(false))
    return () => controller.abort()
  }, [filters, page, pageSize, sort, reloadKey, setPage])

  useEffect(() => {
    if (!openBatch) return
    const controller = new AbortController()
    setMessagesLoading(true)
    setMessages([])
    setMessagesError(null)
    listBatchMessages(openBatch.id, { page: messagesPage, pageSize: messagesPageSize, sort: messagesSort }, controller.signal)
      .then((response) => {
        if (controller.signal.aborted) return
        setMessages(response.rows)
        setMessagesTotal(response.total)
      })
      .catch((error) => {
        if (controller.signal.aborted) return
        setMessagesError(error)
        setMessagesTotal(0)
      })
      .finally(() => !controller.signal.aborted && setMessagesLoading(false))
    return () => controller.abort()
  }, [openBatch, messagesPage, messagesPageSize, messagesSort])

  const reviewDelivery = async (item, confirmedSent) => {
    if (reviewBusy || !deliveryTrace.trim()) return
    setReviewBusy(true)
    try {
      await resolveUncertainDelivery(preview.id, item.messageId, confirmedSent, deliveryTrace)
      toast.success(S.reviewed)
      setPreview(null)
      setDeliveryTrace('')
      setOpenBatch(null)
      setReloadKey((key) => key + 1)
    } catch (error) {
      toast.error(serverDetail(error.message) || S.reviewFailed)
    } finally { setReviewBusy(false) }
  }

  const confirmDelete = async () => {
    const batch = pendingDelete
    setPendingDelete(null)
    if (!batch) return
    try {
      const response = await deleteBatch(batch.id)
      if (response.ok) {
        toast.success(response.message || S.deleted)
        setReloadKey((key) => key + 1)
      } else {
        toast.error(serverDetail(response.message) || S.deleteFailed)
      }
    } catch (error) {
      toast.error(serverDetail(error?.message) || S.deleteFailed)
    }
  }

  const columns = useMemo(
    () => [
      {
        key: 'typeName',
        header: S.columns.type,
        width: '220px',
        render: (row) => <bdi>{row.typeName}</bdi>,
      },
      {
        key: 'state',
        header: S.columns.status,
        width: '140px',
        value: (row) => S.states[row.state] ?? row.state,
        render: (row) => (
          <StatusBadge tone={STATE_TONE[row.state] ?? 'dark'}>
            {S.states[row.state] ?? row.state}
          </StatusBadge>
        ),
      },
      {
        key: 'scheduledStartDate',
        header: S.columns.createdOn,
        render: (row) => (
          <span className="sl-date" dir="ltr">
            {asDate(row.scheduledStartDate)}
          </span>
        ),
      },
      {
        key: 'startDate',
        header: S.columns.startDate,
        render: (row) => (
          <span className="sl-date" dir="ltr">
            {asDate(row.startDate)}
          </span>
        ),
      },
      {
        key: 'endDate',
        header: S.columns.endDate,
        render: (row) => (
          <span className="sl-date" dir="ltr">
            {asDate(row.endDate)}
          </span>
        ),
      },
      {
        key: 'actions',
        header: <List size={15} strokeWidth={1.75} />,
        align: 'center',
        width: '104px',
        sortable: false,
        searchable: false,
        render: (row) => (
          <span className="sl-row-actions">
            <IconButton icon={List} label={S.details} tone="info" onClick={() => {
              setMessagesPage(1)
              setOpenBatch(row)
            }} />
            {/* Only a waiting batch carries the delete button. */}
            {row.state === S.deletableState && (
              <IconButton
                icon={Trash2}
                label={S.delete}
                tone="danger"
                onClick={() => setPendingDelete(row)}
              />
            )}
          </span>
        ),
      },
    ].map((column) => ({ ...column, sortable: BATCH_SORTABLE.includes(column.key) })),
    [],
  )

  const messageColumns = useMemo(
    () => [
      {
        key: 'status',
        header: S.messageColumns.status,
        width: '140px',
        value: (row) => S.states[row.status] ?? row.status,
        render: (row) => (
          <StatusBadge tone={STATE_TONE[row.status] ?? 'dark'}>
            {S.states[row.status] ?? row.status}
          </StatusBadge>
        ),
      },
      {
        key: 'lastTry',
        header: S.messageColumns.lastStatusDate,
        width: '200px',
        render: (row) => (
          <span className="sl-date" dir="ltr">
            {asDateTime(row.lastTry)}
          </span>
        ),
      },
      {
        key: 'details',
        header: S.messageColumns.details,
        value: (row) => row.details.map((item) => item.receiver).join(' '),
        render: (row) => (
          <span className="sl-channels">
            {row.details.map((item, index) => (
              <span key={index}>
                <b dir="ltr">{item.channel}:</b> <span dir="ltr">{item.receiver}</span>
              </span>
            ))}
          </span>
        ),
      },
      {
        key: 'preview',
        header: <Eye size={15} strokeWidth={1.75} />,
        align: 'center',
        width: '72px',
        sortable: false,
        searchable: false,
        render: (row) => (
          <IconButton icon={Eye} label={S.preview} tone="info" onClick={() => {setDeliveryTrace(''); setPreview(row)}} />
        ),
      },
    ].map((column) => ({ ...column, sortable: MESSAGE_SORTABLE.includes(column.key) })),
    [],
  )

  const visibleRows = useMemo(() => {
    const byType = new Map(templates.map((template) => [String(template.type), template.name]))
    return rows.map((row) => ({ ...row, typeName: byType.get(String(row.templateType)) ?? String(row.templateType ?? '') }))
  }, [rows, templates])

  const stateSpread = useMemo(() => {
    const rank = (state) => {
      const index = BAND_ORDER.indexOf(state)
      return index === -1 ? BAND_ORDER.length : index
    }
    return [...stateCounts]
      .sort((a, b) => rank(a.state) - rank(b.state))
      .map(({ state, count }) => ({
        id: state,
        label: S.states[state] ?? state,
        value: count,
        tone: BAND_STATE[state]?.tone,
        icon: BAND_STATE[state]?.icon,
      }))
  }, [stateCounts])

  return (
    <>
      <PageHeader title={S.title} count={loading ? undefined : total} />

      {/* Aggregated across the filtered log by the API, independently of the current page. */}
      {stateSpread.length > 0 && (
        <StatStrip items={stateSpread} label={S.status} variant="band" />
      )}

      {/* The legacy hides this table's own search box and drives filtering from a
          menu; the box stays hidden, but the two filters are inline and every
          applied one stays up as a chip. */}
      <FilterBar
        chips={[
          filters.type !== '' && {
            id: 'type',
            label: S.type,
            value: templates.find((template) => String(template.type) === String(filters.type))?.name ?? '',
            onRemove: () => {
              setDraft((previous) => ({ ...previous, type: '' }))
              setFilters((previous) => ({ ...previous, type: '' }))
            },
          },
          filters.status !== '' && {
            id: 'status',
            label: S.status,
            value: S.states[filters.status] ?? filters.status,
            onRemove: () => {
              setDraft((previous) => ({ ...previous, status: '' }))
              setFilters((previous) => ({ ...previous, status: '' }))
            },
          },
        ].filter(Boolean)}
        resetLabel={S.reset}
        onReset={() => {
          setDraft({ type: '', status: '' })
          setFilters({ type: '', status: '' })
        }}
      >
        <Select
          id="typeFilter"
          value={draft.type}
          onChange={(value) => {
            setDraft((previous) => ({ ...previous, type: value }))
            setFilters((previous) => ({ ...previous, type: value }))
          }}
          placeholder={S.type}
          aria-label={S.type}
          options={templates.map((template) => ({ value: template.type, label: template.name }))}
        />
        <Select
          id="statusFilter"
          value={draft.status}
          onChange={(value) => {
            setDraft((previous) => ({ ...previous, status: value }))
            setFilters((previous) => ({ ...previous, status: value }))
          }}
          placeholder={S.status}
          aria-label={S.status}
          options={STATE_OPTIONS}
        />
      </FilterBar>

      <DataTable
        columns={columns}
        rows={visibleRows}
        loading={loading}
        error={loadError}
        /* The batch's details, as its own details button opens them. */
        onRowClick={(row) => {
          setMessagesPage(1)
          setOpenBatch(row)
        }}
        server={{
          page,
          pageSize,
          total,
          sort,
          onChange: (next) => {
            setPage(next.page)
            setPageSize(next.pageSize)
            setSort(next.sort)
          },
        }}
        searchable={false}
        controls={false}
        getRowId={(row) => row.id}
        /* The filters live outside the table, so it cannot see the row set change on its own:
           choosing a type kept page 4, which the filtered log may not have. */
        resetKey={JSON.stringify(filters)}
      />

      {/* The batch's recipients — a drawer, so the log stays behind it, and titled
          by the batch rather than by the word `التفاصيل`. */}
      <Drawer
        dialog
        open={!!openBatch}
        title={openBatch?.typeName ?? S.details}
        subtitle={openBatch ? S.states[openBatch.state] : undefined}
        size="lg"
        onClose={() => setOpenBatch(null)}
      >
        <DataTable
          /* A list inside a sheet is opened to answer one question; it starts at the top, and
             the address belongs to the log behind it. */
          stateKey={false}
          columns={messageColumns}
          rows={messages}
          loading={messagesLoading}
          error={messagesError}
          searchable={false}
          controls={false}
          server={{
            page: messagesPage,
            pageSize: messagesPageSize,
            total: messagesTotal,
            sort: messagesSort,
            onChange: (next) => {
              setMessagesPage(next.page)
              setMessagesPageSize(next.pageSize)
              setMessagesSort(next.sort)
            },
          }}
          getRowId={(row) => row.id}
        />
      </Drawer>

      {/* Was a raw JSON dump in a <pre> — a developer artefact in an admin panel.
          Same payload, as the definition list it always was. */}
      <Modal
        open={!!preview}
        title={S.messageColumns.details}
        onClose={() => {if (!reviewBusy) {setPreview(null); setDeliveryTrace('')}}}
      >
        <dl className="sl-payload">
          {(preview?.details ?? []).map((item, index) => (
            <div key={index} className="sl-payload__row">
              <dt dir="ltr">{item.channel}</dt>
              <dd>
                <bdi dir="ltr">{item.receiver}</bdi>{item.error && <p><bdi>{item.error}</bdi></p>}
                {item.messageId && <p><span>{S.messageId}: </span><code dir="ltr">{item.messageId}</code></p>}
                {item.response === 'Uncertain' && item.messageId && <div className="sl-delivery-review">
                  <p>{S.reviewHint}</p>
                  <label htmlFor={`delivery-trace-${index}`}>{S.traceReference}</label>
                  <TextField id={`delivery-trace-${index}`} value={deliveryTrace} onChange={setDeliveryTrace} maxLength={256} />
                  <div className="sl-delivery-review__actions">
                    <Button size="sm" disabled={reviewBusy || !deliveryTrace.trim()} onClick={() => reviewDelivery(item, true)}>{S.confirmSent}</Button>
                    <Button size="sm" variant="soft" disabled={reviewBusy || !deliveryTrace.trim()} onClick={() => reviewDelivery(item, false)}>{S.confirmNotSent}</Button>
                  </div>
                </div>}
              </dd>
            </div>
          ))}
          <div className="sl-payload__row">
            <dt>
              <bdi>{S.messageColumns.status}</bdi>
            </dt>
            <dd>
              <bdi>{preview ? (S.states[preview.status] ?? preview.status) : ''}</bdi>
            </dd>
          </div>
          <div className="sl-payload__row">
            <dt>
              <bdi>{S.messageColumns.lastStatusDate}</bdi>
            </dt>
            <dd dir="ltr">{preview?.lastTry ? preview.lastTry.replace('T', ' ') : ''}</dd>
          </div>
        </dl>
      </Modal>

      <ConfirmDialog
        open={!!pendingDelete}
        message={S.deleteConfirm}
        confirmLabel={S.yes}
        cancelLabel={S.no}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />

    </>
  )
}
